"use client";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import type { ModalContentByType } from "@lib/contentful/types";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, use, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { safeDeserialize } from "@lib/utils/safeDeserialize";
import type { SerializedValue } from "@ungap/structured-clone";
import { modalMap } from "./types";
import { useAuth } from "@lib/auth/useAuth";
import { useAuthFormStore } from "@store/authFormStore";

const lazyModalTypes = new Set<ModalTypes>([
    ModalTypes.contactForm,
    ModalTypes.contactFormSubmitted,
    ModalTypes.giftWrapping,
    ModalTypes.sizeGuide,
    ModalTypes.globalSizeGuide,
    ModalTypes.shippingDetails,
    ModalTypes.accountDeactivation,
]);

type GlobalModalContextType = {
    showModal: (modalType: string, modalProps?: ModalProps) => void;
    hideModal: () => void;
    modalType: ModalTypes;
    modalProps: ModalProps | null;
    isReady: boolean;
};

type ModalProps = Record<string, unknown> & {
    microcopies?: Record<string, string>;
};

const GlobalModalContext = createContext<GlobalModalContextType | null>(null);
export const useGlobalModalContext = () => {
    const context = use(GlobalModalContext);

    if (!context) {
        throw new Error("useGlobalModalContext must be used within a GlobalModalProvider");
    }
    return context;
};

interface GlobalModalProviderProps {
    children: ReactNode;
}

const getLazyModalProps = (modalContent: ModalContentByType) => {
    switch (modalContent.modalType) {
        case ModalTypes.contactForm:
        case ModalTypes.contactFormSubmitted:
            return modalContent.content;
        case ModalTypes.giftWrapping:
            return {
                giftWrappingModal: modalContent.content.giftWrappingDescription,
            };
        case ModalTypes.sizeGuide:
        case ModalTypes.globalSizeGuide:
            return { sizeGuideModal: modalContent.content.sizeGuideModal };
        case ModalTypes.shippingDetails:
            return {
                shippingDetailsModal: modalContent.content.shippingDetailsModal,
            };
        case ModalTypes.accountDeactivation:
            return modalContent.content;
        default: {
            const exhaustiveModalContent: never = modalContent;
            return exhaustiveModalContent;
        }
    }
};

export const GlobalModalProvider: FC<GlobalModalProviderProps> = ({ children }) => {
    const locale = useLocale();

    const { isLoading: isAuthLoading, requiresSocialRegistration } = useAuth();

    const initialReturnModalType = useRef(useAuthFormStore.getState().returnModalType);
    const setReturnModal = useAuthFormStore((state) => state.setReturnModal);
    const hasHandledReturnModal = useRef(false);

    const initialPendingSignupMethod = useRef(useAuthFormStore.getState().pendingSignupMethod);
    const setPendingSignupMethod = useAuthFormStore((state) => state.setPendingSignupMethod);
    const hasHandledPendingSignupMethod = useRef(false);

    const [modalType, setModalType] = useState<ModalTypes>(null);
    const [modalProps, setModalProps] = useState<ModalProps | null>(null);
    const [isReady, setIsReady] = useState<boolean>(false);
    const modalContentCache = useRef(new Map<ModalTypes, ModalContentByType>());
    const modalRequestId = useRef(0);
    const { siteSettings } = useGlobalContent();

    const fetchModalContent = useCallback(
        async (requestedModalType: ModalTypes): Promise<ModalContentByType | null> => {
            const cached = modalContentCache.current.get(requestedModalType);
            if (cached) return cached;

            const params = new URLSearchParams({ modalType: requestedModalType, locale });
            const response = await fetch(`/api/modal-content?${params}`);
            if (!response.ok) return null;

            const body = (await response.json()) as { content: SerializedValue };
            const content = safeDeserialize<ModalContentByType>(body.content);
            modalContentCache.current.set(requestedModalType, content);
            return content;
        },
        [locale]
    );

    const hideModal = useCallback(() => {
        modalRequestId.current += 1;
        setModalType(null);
        setModalProps(null);
    }, []);

    const openLazyModal = useCallback(
        async (nextModalType: ModalTypes, nextModalProps: ModalProps, requestId: number) => {
            try {
                const modalContent = await fetchModalContent(nextModalType);

                if (!modalContent || requestId !== modalRequestId.current) return;

                const lazyModalProps = getLazyModalProps(modalContent);
                setModalProps({ ...lazyModalProps, ...nextModalProps });
                setModalType(nextModalType);
            } catch {
                // The modal stays closed when its CMS content cannot be loaded.
            }
        },
        [fetchModalContent]
    );

    const showModal = useCallback(
        (_modalType: ModalTypes, _modalProps: ModalProps = {}) => {
            if (!lazyModalTypes.has(_modalType)) {
                modalRequestId.current += 1;
                setModalProps(_modalProps);
                setModalType(_modalType);
                return;
            }

            const requestId = ++modalRequestId.current;
            void openLazyModal(_modalType, _modalProps, requestId);
        },
        [openLazyModal]
    );

    useEffect(() => {
        setIsReady(true);
    }, []);

    // Handle the case where a user is redirected back to the site after signing up with a social provider.
    useEffect(() => {
        if (
            !isReady ||
            isAuthLoading ||
            hasHandledReturnModal.current ||
            initialReturnModalType.current !== ModalTypes.createAccount
        ) {
            return;
        }

        hasHandledReturnModal.current = true;
        setReturnModal(null);

        if (requiresSocialRegistration) {
            showModal(ModalTypes.createAccount);
        }
    }, [isAuthLoading, isReady, requiresSocialRegistration, setReturnModal, showModal]);

    useEffect(() => {
        if (!isReady || isAuthLoading || hasHandledPendingSignupMethod.current) {
            return;
        }
        hasHandledPendingSignupMethod.current = true;
        if (initialPendingSignupMethod.current) {
            AnalyticsClient.clubSignup({
                stepName: "signup_method_selected",
                signupMethod: initialPendingSignupMethod.current,
            });
            setPendingSignupMethod(null);
        }
    }, [isAuthLoading, isReady, setPendingSignupMethod]);

    useEffect(() => {
        modalRequestId.current += 1;
        modalContentCache.current.clear();
        setModalType(null);
        setModalProps(null);
    }, [locale]);

    const getModalProps = (): ModalProps => {
        const authorizationModules = {
            offerToLogIn: siteSettings.auth.offerToLogIn,
            offerToCreateAnAccount: siteSettings.auth.offerToCreateAnAccount,
            policiesAgreementTerms: siteSettings.auth.policiesAgreementTerms,
            authorizationInvitation: siteSettings.auth.authorizationInvitation,
            forgotYourPassword: siteSettings.auth.forgotYourPassword,
            eccoNewsletter: siteSettings.auth.newsletter,
            eccoMembership: siteSettings.auth.membership,
        };

        switch (modalType) {
            case ModalTypes.createAccount:
            case ModalTypes.login:
            case ModalTypes.forgotPassword:
            case ModalTypes.upgradeAccount:
                return { ...modalProps, ...authorizationModules };
            case ModalTypes.thankYouForSignUp:
                return { profileLinks: siteSettings.account.profileLinks, ...modalProps };
            case ModalTypes.sizeGuide:
            case ModalTypes.globalSizeGuide:
                return {
                    isOpen: true,
                    onClose: hideModal,
                    target: modalProps?.target,
                    ...modalProps,
                };
            case ModalTypes.shippingDetails:
                return { isOpen: true, onClose: hideModal, ...modalProps };
            case ModalTypes.addToWishList:
                return {
                    isOpen: true,
                    onClose: hideModal,
                    ...authorizationModules,
                    ...modalProps,
                };
            case ModalTypes.accountDeactivation:
                return { ...modalProps, eccoNewsletter: authorizationModules?.eccoNewsletter };
            case ModalTypes.returnLabel:
                return { returnLabel: siteSettings.product.returnLabel, ...modalProps };
            default:
                return modalProps;
        }
    };

    const ModalComponent = modalMap[modalType];

    const providerValue = useMemo(
        () => ({ modalType, modalProps, showModal, hideModal, isReady }),

        [hideModal, modalProps, modalType, showModal, isReady]
    );

    return (
        <GlobalModalContext.Provider value={providerValue}>
            {ModalComponent && <ModalComponent {...getModalProps()} />}
            {children}
        </GlobalModalContext.Provider>
    );
};
